import { Link } from "react-router-dom";

import { FAQS } from "./content";

/**
 * The canvas's `FAQ` screen.
 *
 * Structurally the same accordion as Trust, but not shared with it: the canvas
 * gives this one a narrower measure (820px), a centred header, a smaller row
 * face (16.5px against 17.5px) and 21px rows against 22px. Folding the two into
 * one component would mean four props whose only job is to remember which
 * screen is calling — the duplication is cheaper than that.
 *
 * The top padding is 30px rather than a section-sized value because this screen
 * is designed to sit directly under Trust and read as its continuation.
 */

interface FaqProps {
  openIndex: number;
  onToggle: (i: number) => void;
}

export default function Faq({ openIndex, onToggle }: FaqProps) {
  return (
    <section
      id="faq"
      className="site-gutter"
      style={{
        paddingTop: 30,
        paddingBottom: 130,
        position: "relative",
        overflow: "clip",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div data-rv="" style={{ textAlign: "center", marginBottom: 46 }}>
          <h2
            style={{
              margin: "0 0 14px",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: "clamp(26px, 3vw, 40px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.16,
            }}
          >
            Questions worth asking first.
          </h2>
          <p
            style={{
              margin: "0 auto 20px",
              maxWidth: 520,
              fontSize: 16,
              color: "rgba(var(--ink-rgb),.66)",
            }}
          >
            If yours isn&rsquo;t here, ask it directly, and we would rather
            answer it than have you guess.
          </p>
          {/* Straight to the contact page. Pointing at the closing band only
              moved the visitor to another button. */}
          <Link
            to="/contact"
            className="dc-h-ghost"
            style={{
              display: "inline-block",
              border: "1px solid rgba(var(--ink-rgb),.3)",
              borderRadius: 999,
              padding: "10px 22px",
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--ink)",
              transition: "border-color .2s, background .2s",
            }}
          >
            Ask us anything
          </Link>
        </div>
        <div
          data-rv=""
          style={{ borderTop: "1px solid rgba(var(--ink-rgb),.2)" }}
        >
          {FAQS.map((f, i) => {
            const open = openIndex === i;
            return (
              <div
                key={f.q}
                style={{ borderBottom: "1px solid rgba(var(--ink-rgb),.2)" }}
              >
                <button
                  type="button"
                  onClick={() => onToggle(i)}
                  aria-expanded={open}
                  className="dc-h-link"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 20,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "21px 4px",
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 16.5,
                    fontWeight: 600,
                    color: "var(--ink)",
                    textAlign: "left",
                  }}
                >
                  {f.q}
                  <span
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 18,
                      color: "var(--acc2)",
                      fontWeight: 400,
                    }}
                  >
                    {open ? "−" : "+"}
                  </span>
                </button>
                {open && (
                  <p
                    style={{
                      margin: 0,
                      padding: "0 40px 22px 4px",
                      fontSize: 15,
                      color: "rgba(var(--ink-rgb),.68)",
                    }}
                  >
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
